import { useMutation } from "@tanstack/react-query"
import { changePassword } from "api/account.api"
import { useToast } from "hooks"
import { useState } from "react"
import styled from "styled-components"
import { ButtonPrimary } from "ui/base/Button/ButtonStyled"
import { InputSignUp } from "ui/base/InputSignUp/InputSignUp"
import { Modal } from "ui/base/modal/Modal"
import { LockIcon } from "ui/icons"
import { fonts, mediaSizes } from "ui/theme"
import { passwordValidation } from "utils"

const MSG_PASSWORD_VALIDATION =
    "Password must be 6-20 characters with at least 1 uppercase, 1 lowercase, 1 number, and 1 special character. No spaces allowed."

export const ChangePassword = () => {
    const [formData, setFormData] = useState({
        password: "",
        confirmPassword: "",
    })
    const [errors, setErrors] = useState({})
    const [open, setOpen] = useState(false)

    const { mutate } = useMutation({
        mutationFn: changePassword,
        onSuccess: () => {
            useToast("success", "Your password has been changed")
            setOpen(false)
        },
        onError: (error) => {
            useToast("error", error?.response?.data?.message || "Error: changePass")
        },
    })

    const updateFormData = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }))
        setErrors((prev) => ({ ...prev, password: {}, confirmPassword: {} }))
    }

    const validateForm = () => {
        const newErrors = {}
        if (formData.password !== formData.confirmPassword) {
            newErrors.password = newErrors.confirmPassword = {
                value: true,
                text: "Passwords must match",
            }
        } else if (!passwordValidation(formData.password)) {
            newErrors.password = newErrors.confirmPassword = {
                value: true,
                text: MSG_PASSWORD_VALIDATION,
            }
        }
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const onSubmit = () => {
        if (validateForm()) {
            mutate(formData.password)
        }
    }

    const onClose = () => {
        setOpen(false)
        setFormData({
            password: "",
            confirmPassword: "",
        })
    }

    return (
        <>
            <ResetPasswordButton onClick={() => setOpen(true)}>
                Change password
            </ResetPasswordButton>
            <Modal
                show={open}
                handleClose={onClose}
                minHeight={200}
                maxWidth={450}>
                <Container>
                    <Title>Change Password</Title>
                    <Message>{MSG_PASSWORD_VALIDATION}</Message>
                    <InputContainer>
                        <InputSignUp
                            id='password'
                            value={formData.password}
                            type='password'
                            placeholder='New password'
                            icon={<LockIcon />}
                            setValue={(input) => updateFormData("password", input)}
                            maxLength={20}
                            isError={errors.password?.value}
                            errorText={errors.password?.text}
                        />
                        <InputSignUp
                            id='confirmPassword'
                            value={formData.confirmPassword}
                            type='password'
                            placeholder='Confirm new password'
                            icon={<LockIcon />}
                            setValue={(input) => updateFormData("confirmPassword", input)}
                            maxLength={20}
                            isError={errors.confirmPassword?.value}
                            errorText={errors.confirmPassword?.text}
                        />
                    </InputContainer>
                    <ButtonContainer>
                        <ButtonPrimary onClick={onSubmit}>Save</ButtonPrimary>
                        <ButtonPrimary
                            onClick={onClose}
                            variant='primary_fade'>
                            Cancel
                        </ButtonPrimary>
                    </ButtonContainer>
                </Container>
            </Modal>
        </>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    font-family: Montserrat;
`

const Title = styled.h3`
    font-size: ${fonts.sizes.big};
    font-weight: 600;
`

const Message = styled.p`
    font-size: ${fonts.sizes.regular};
    font-weight: 500;
    text-align: center;
`

const InputContainer = styled.div`
    display: grid;
    gap: 1rem;
    width: 100%;
`

const ButtonContainer = styled.div`
    display: flex;
    gap: 0.5rem;
    width: 100%;

    & > * {
        width: 100%;
    }
`

const ResetPasswordButton = styled.button`
    all: unset;
    cursor: pointer;
    font-weight: 600;
    text-align: left;
    font-size: ${fonts.sizes.small};

    &:hover {
        text-decoration: underline;
    }

    @media (width >= ${mediaSizes.md}) {
        font-size: ${fonts.sizes.regular};
    }

    @media (width >= ${mediaSizes.lg}) {
        font-size: ${fonts.sizes.medium};
    }
`
